import React from 'react';
import { Phone, Shield, AlertTriangle, CheckCircle, AlertOctagon, Send, Activity } from 'lucide-react';
import { Button } from '../ui/button';

const SIMSwapTab = ({ simSwapForm, setSimSwapForm, testSimSwap, testResult, simSwapAlerts, loading }) => (
  <div className="space-y-6">
          <div className="bg-white rounded-xl border shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">SIM Swap Attack Detection</h3>
              <Button onClick={() => setShowSimSwapTest(!showSimSwapTest)} variant="outline">
                {showSimSwapTest ? 'Hide Test' : 'Test Detection'}
              </Button>
            </div>
            
            {showSimSwapTest && (
              <form onSubmit={testSimSwapDetection} className="space-y-4 mb-6 p-4 bg-gray-50 rounded-lg">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subscriber ID</label>
                    <input
                      type="text"
                      value={simSwapForm.subscriber_id}
                      onChange={(e) => setSimSwapForm({ ...simSwapForm, subscriber_id: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                    <input
                      type="text"
                      value={simSwapForm.phone_number}
                      onChange={(e) => setSimSwapForm({ ...simSwapForm, phone_number: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Old IMSI</label>
                    <input
                      type="text"
                      value={simSwapForm.old_imsi}
                      onChange={(e) => setSimSwapForm({ ...simSwapForm, old_imsi: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">New IMSI</label>
                    <input
                      type="text"
                      value={simSwapForm.new_imsi}
                      onChange={(e) => setSimSwapForm({ ...simSwapForm, new_imsi: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Location Before</label>
                    <input
                      type="text"
                      value={simSwapForm.location_before}
                      onChange={(e) => setSimSwapForm({ ...simSwapForm, location_before: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Location After</label>
                    <input
                      type="text"
                      value={simSwapForm.location_after}
                      onChange={(e) => setSimSwapForm({ ...simSwapForm, location_after: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={simSwapForm.carrier_change}
                      onChange={(e) => setSimSwapForm({ ...simSwapForm, carrier_change: e.target.checked })}
                      className="rounded"
                    />
                    <span className="text-sm">Carrier Changed</span>
                  </label>
                  <Button type="submit" className="bg-purple-600 hover:bg-purple-700">
                    Run Detection
                  </Button>
                </div>
              </form>
            )}
            
            {/* Test Result */}
            {testResult?.type === 'sim_swap' && (
              <div className={`p-4 rounded-lg mb-4 ${
                testResult.data.is_sim_swap_detected ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  {testResult.data.is_sim_swap_detected ? (
                    <AlertTriangle className="h-5 w-5 text-red-600" />
                  ) : (
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  )}
                  <span className="font-semibold">
                    {testResult.data.is_sim_swap_detected ? 'SIM SWAP DETECTED!' : 'No SIM Swap Detected'}
                  </span>
                  <span className={`px-2 py-0.5 rounded text-xs ${getSeverityColor(testResult.data.threat_level)}`}>
                    {testResult.data.threat_level?.toUpperCase()}
                  </span>
                </div>
                <p className="text-sm">Risk Score: <strong>{testResult.data.risk_score}</strong></p>
                {testResult.data.risk_factors?.length > 0 && (
                  <div className="mt-2">
                    <p className="text-sm font-medium">Risk Factors:</p>
                    {testResult.data.risk_factors.map((f, i) => (
                      <p key={f.description} className="text-xs text-gray-600">• {f.description}</p>
                    ))}
                  </div>
                )}
                {testResult.data.recommended_actions?.length > 0 && (
                  <div className="mt-2">
                    <p className="text-sm font-medium">Recommended Actions:</p>
                    {testResult.data.recommended_actions.map((a, i) => (
                      <p key={a} className="text-xs text-blue-600">✓ {a}</p>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Recent Alerts */}
            <h4 className="font-medium text-gray-900 mb-3">Recent SIM Swap Alerts</h4>
            {simSwapAlerts.length === 0 ? (
              <p className="text-gray-500 text-sm">No SIM swap alerts detected yet</p>
            ) : (
              <div className="space-y-2">
                {simSwapAlerts.map((alert) => (
                  <div key={alert.id} className={`p-3 rounded-lg border ${getSeverityColor(alert.threat_level)}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Alert #{alert.id?.slice(0, 8)}</span>
                      <span className="text-xs">{alert.threat_level?.toUpperCase()} - Score: {alert.risk_score}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
);

export default SIMSwapTab;
